import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import { useMemo } from 'react';
import {
  useSellerHierarchies,
  useSellerTagAssignments,
  useSellerTagTypes,
} from '../../../lib/query/hooks';
import { downloadCSV } from '../../../lib/utils';
import type { SimpleAccount } from '@/lib/types';
import {
  buildSellerHierarchyCsvRows,
  buildSellerRowsWithTags,
  buildTagCsvHeaders,
} from './seller-tag/lib/sellerTagColumns';

interface SellerHierarchyListProps {
  account: SimpleAccount;
}

/**
 * 店舗階層抽出タブのコンテンツ（FKV3-12590 / 店舗タグの列は FKV3-13002）
 * 同期APIで全店舗を取得し一覧表示、CSVはブラウザ内で生成する（ProductGroupTreeListと同じ構成）。
 *
 * 店舗階層のあとに、タグ種別ごとの列（列名＝タグ種別名・値＝付与されているタグ名）を並べる。
 * 商品マスタ抽出の商品グループ列と同じ見え方に揃えている。
 */
export function SellerHierarchyList({ account }: SellerHierarchyListProps) {
  const {
    data: sellerHierarchies = [],
    isLoading: loading,
    error,
  } = useSellerHierarchies(account.code);

  const {
    data: tagTypeData,
    isLoading: tagTypesLoading,
    error: tagTypesError,
  } = useSellerTagTypes(account.code);
  const {
    data: assignmentData,
    isLoading: assignmentsLoading,
    error: assignmentsError,
  } = useSellerTagAssignments(account.code);

  const tagTypes = useMemo(() => tagTypeData?.tag_types ?? [], [tagTypeData]);
  // タグの取得に失敗しても isLoading は false に戻り、data が undefined のまま
  // 「タグ種別0件」と見分けがつかなくなる。欠けたまま黙って出さないよう失敗も見る
  const tagsError = tagTypesError ?? assignmentsError;
  const tagsReady = !tagTypesLoading && !assignmentsLoading && !tagsError;

  const rows = useMemo(
    () => buildSellerRowsWithTags(sellerHierarchies, tagTypes, assignmentData?.assignments ?? []),
    [sellerHierarchies, tagTypes, assignmentData],
  );
  const tagHeaders = useMemo(() => buildTagCsvHeaders(tagTypes), [tagTypes]);

  const handleDownloadCSV = () => {
    const filename = `店舗階層一覧_${account.code}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(buildSellerHierarchyCsvRows(rows, tagTypes), filename);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>店舗階層一覧を読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error instanceof Error ? error.message : '店舗階層一覧の読み込みに失敗しました。'}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">店舗階層一覧 ({sellerHierarchies.length}件)</Typography>
            {sellerHierarchies.length > 0 && (
              // タグが揃う前に落とすとタグ列が欠けたCSVになるため、揃うまで押させない
              <Button
                variant="outlined"
                onClick={handleDownloadCSV}
                size="small"
                disabled={!tagsReady}
              >
                CSVでダウンロード
              </Button>
            )}
          </Box>

          {/* 店舗階層そのものは出せるので一覧は表示し、タグ列が欠けていることだけ知らせる */}
          {tagsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              店舗タグの読み込みに失敗したため、タグ種別の列を表示できていません。
              CSVのダウンロードは、タグを含まない不完全な出力を防ぐため無効にしています。
            </Alert>
          )}

          {sellerHierarchies.length === 0 ? (
            <Alert severity="info">店舗階層データが見つかりませんでした。</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto', maxWidth: '100%' }}>
              <Table size="small" sx={{ whiteSpace: 'nowrap' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗ID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗階層</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>郵便番号</TableCell>
                    {tagTypes.map((type, i) => (
                      <TableCell key={type.id} sx={{ whiteSpace: 'nowrap' }}>
                        {tagHeaders[i]}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`${row.sellerId || row.sellerCustomerId}-${index}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.sellerCustomerId || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.sellerName || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.hierarchy || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.postCode || '-'}</TableCell>
                      {tagTypes.map((type) => (
                        // 未設定は空欄（店舗階層の「-」と違い、値が無いことを列で見分けたい）
                        <TableCell key={type.id} sx={{ whiteSpace: 'nowrap' }}>
                          {row.tagNames.get(type.id) ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
